import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Loader, SearchableSelect, SearchableSelectOption, SearchableSelectOptionGroup } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useSizeTypes } from 'contexts';
import { SelectedLocationContext } from 'contexts/selected-location';
import dayjs from 'dayjs';
import { default as tz } from 'dayjs/plugin/timezone';
import { default as utc } from 'dayjs/plugin/utc';
import { useContractAPI } from 'hooks/use-contract-api';
import { useCustomerAPI } from 'hooks/use-customer-api';
import {
  FC,
  FormEventHandler,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getContract } from 'services/contracts';
import { Contract, Customer, defaultContract } from 'types';
import { isoDescriptions } from 'utils';
import './edit-contract.scss';

dayjs.extend(utc);
dayjs.extend(tz);

type EditContractContextType = {
  contract: Contract;
  customers: Customer[];
  editContract: (partial: Partial<Contract>) => void;
};

const EditContractContext = createContext<EditContractContextType>({
  contract: defaultContract(),
  customers: [],
  editContract: () => {}
});

const CATCH_ALL_OPTIONS: SearchableSelectOption[] = [
  { label: '* - Default If No Better Match', value: '*' },
  { label: '1* - All 10 Foot Units', value: '1*' },
  { label: '2* - All 20 Foot Units', value: '2*' },
  { label: '4* - All 40/45 Foot Units', value: '4*' },
  { label: '5* - All 50 Foot Units', value: '5*' }
];

const ISO_CODE_OPTIONS: SearchableSelectOptionGroup = {
  groupLabel: 'ISO Codes',
  value: Object.entries(isoDescriptions).map(([value, description]) => ({
    label: `${value} - ${description}`,
    value
  }))
};

const EditContract = () => {
  // STATE
  const navigate = useNavigate();
  const params = useParams();
  const [contractId, setContractId] = useState<number | undefined>(undefined);
  const [loadingContract, setLoadingContract] = useState(false);
  const [contract, setContract] = useState<Contract>(defaultContract());
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [formValid, setFormValid] = useState(false);
  const [startDateValid, setStartDateValid] = useState(true);
  const { selectedLocation } = useContext(SelectedLocationContext);
  const { getAllCustomers } = useCustomerAPI();
  const { containerSizeTypeEntries, chassisSizeTypeEntries, gensetSizeTypeEntries } = useSizeTypes();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { createContract, updateContract } = useContractAPI();

  const unitSizeTypeOptions: SearchableSelectOptionGroup[] = useMemo(() => {
    const toGroup = (groupLabel: string, entries: [string, string[]][]): SearchableSelectOptionGroup => ({
      groupLabel,
      value: entries.filter(e => e[1].length === 1).map(([label, [value]]) => ({ label, value }))
    });
    return [
      { groupLabel: 'Catch-All', value: CATCH_ALL_OPTIONS },
      toGroup('Container Size Types', containerSizeTypeEntries),
      toGroup('Chassis Size Types', chassisSizeTypeEntries),
      toGroup('Genset Size Types', gensetSizeTypeEntries),
      ISO_CODE_OPTIONS
    ];
  }, [containerSizeTypeEntries, chassisSizeTypeEntries, gensetSizeTypeEntries]);

  // CALLBACKS
  const editContract = useCallback((partial: Partial<Contract>) => {
    setContract(p => ({ ...p, ...partial }));
  }, []);

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (contractId === undefined) navigate(`/contracts`, { replace: true });
    else navigate(`/contracts/${contractId}`, { replace: true });
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      if (!formValid || isSubmitting) return;
      setIsSubmitting(true);
      if (contractId === undefined) {
        // New contract
        const response = await createContract(contract);
        if (response) {
          navigate(`/contracts/${response.id}`);
        }
      } else {
        // Update contract
        const response = await updateContract(contract, contractId);
        if (response) {
          navigate(`/contracts/${response.id}`);
        }
      }
      setIsSubmitting(false);
    },
    [contract, contractId, createContract, formValid, isSubmitting, navigate, updateContract]
  );

  // EFFECTS
  // Load Customers
  useEffect(() => {
    if (selectedLocation?.id) {
      getAllCustomers(selectedLocation?.id, false).then(customers => {
        if (!!customers) {
          setCustomers(customers.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase())));
        }
      });
    }
  }, [getAllCustomers, selectedLocation?.id, setCustomers]);

  // Load Contract
  useEffect(() => {
    // If no contractId path param, this is a new contract
    // TODO: add a way to create a new contract based on an existing one. ID in path or query param, maybe?
    const parsedContractId = Number(params.contractId);
    setContractId(isNaN(parsedContractId) ? undefined : parsedContractId);
    if (contractId === undefined) {
      setContract(defaultContract());
      return;
    }
    // Valid contractId number param found. Edit this contract
    // TODO: load passed data if it exists, to save a rest call
    setLoadingContract(true);
    getContract(contractId).then(contract => {
      if (!!contract) {
        // Editing existing contract
        setContract(contract);
      } else {
        // Creating new contract
        setContract(defaultContract());
      }
      setLoadingContract(false);
    });
  }, [contractId, params]);

  // Update validity on model change
  useEffect(() => {
    const valid =
      (formRef.current?.checkValidity() ?? false) &&
      !!contract.customer?.id &&
      !!contract.location?.id &&
      startDateValid &&
      unitSizeTypeOptions.flatMap(t => t.value.map(v => v.value)).includes(contract.unitSizeType);
    setFormValid(valid);
  }, [contract, startDateValid, unitSizeTypeOptions]);

  // Set contract location
  useEffect(() => {
    if (params.contractId) return;
    setContract(previous => ({
      ...previous,
      location: selectedLocation ?? ({} as Contract['location']),
      customer: {} as Customer,
      startDate: dayjs()
        .tz(selectedLocation?.timeZone || 'UTC')
        .startOf('month')
        .toISOString()
    }));
    setStartDateValid(true);
  }, [params.contractId, selectedLocation]);

  if (!contract) {
    return <></>;
  }

  return (
    <>
      <Loader loading={loadingContract}>
        <form ref={formRef} onSubmit={handleSubmit} className="page edit-contract-component">
          <div className="page-header">
            <h1 className="text-uppercase">{contractId === undefined ? 'New Contract' : `Contract ${contractId}`}</h1>
            <div className="d-flex g-3">
              <button type="button" className="btn-outline" onClick={handleCancel} formNoValidate>
                CANCEL
              </button>
              <Loader loading={isSubmitting}>
                <button disabled={!formValid || isSubmitting} className="btn-primary" type="submit">
                  SAVE
                </button>
              </Loader>
            </div>
          </div>
          <div>
            <div className="grid-big-4">
              <EditContractContext.Provider
                value={{
                  contract,
                  customers,
                  editContract
                }}
              >
                <GeneralInfoPanel
                  unitSizeTypeOptions={unitSizeTypeOptions}
                  onStartDateValidityChange={setStartDateValid}
                />
                <EmptyContainerAndChassisCard />
                <EmptyBoxOnlyCard />
                <ChassisOnlyCard />
                <LoadedUnitCard />
              </EditContractContext.Provider>
            </div>
          </div>
        </form>
        {contractId === undefined ? (
          <ErrorDialog title="Create Contract Failed" />
        ) : (
          <ErrorDialog title="Update Contract Failed" />
        )}
      </Loader>
    </>
  );
};

const GeneralInfoPanel: FC<{
  unitSizeTypeOptions: SearchableSelectOption[] | SearchableSelectOptionGroup[];
  onStartDateValidityChange: (valid: boolean) => void;
}> = ({ unitSizeTypeOptions, onStartDateValidityChange }) => {
  const context = useContext(EditContractContext);
  const timeZone = context.contract.location?.timeZone || 'UTC';

  function handleStartDateChange(val: dayjs.Dayjs | null) {
    // Ignore null/invalid input (e.g. backspacing or typing non-numeric chars mid-edit):
    // calling toISOString() on an invalid dayjs throws RangeError: Invalid time value.
    onStartDateValidityChange(!!val?.isValid());
    if (val && val.isValid()) {
      // Reparse the selected calendar day in the location zone so DST changes cannot retain
      // the old offset and accidentally submit the previous day to REST.
      context.editContract({ startDate: dayjs.tz(val.format('YYYY-MM-DD'), timeZone).toISOString() });
    }
  }

  return (
    <div className="panel justify-content-between col-span-4">
      <div className="grid-4 p-3" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <div>
          <label className="required">Customer</label>
          <div className="mt-2">
            <SearchableSelect
              options={context.customers.map<SearchableSelectOption>(c => ({ label: c.name, value: `${c.id}` }))}
              value={`${context.contract.customer?.id ?? ''}`}
              onValueSelected={idString =>
                context.editContract({
                  customer: context.customers.find(c => `${c.id}` === idString) ?? ({} as Customer)
                })
              }
            />
          </div>
        </div>
        <div>
          <label className="required">Unit Size Type</label>
          <div className="mt-2">
            <SearchableSelect
              value={context.contract.unitSizeType}
              options={unitSizeTypeOptions}
              onValueSelected={val => context.editContract({ unitSizeType: val })}
            />
          </div>
        </div>
        <div>
          <label>Start Date</label>
          <div className="mt-2">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="Start Date"
                timezone={timeZone}
                slotProps={{ textField: { margin: 'none', required: true, size: 'small' } }}
                value={!!context.contract.startDate ? dayjs(context.contract.startDate).tz(timeZone) : null}
                sx={{ backgroundColor: 'var(--white)' }}
                onChange={handleStartDateChange}
                onError={error => onStartDateValidityChange(error === null)}
              />
            </LocalizationProvider>
          </div>
        </div>
        <div>
          <label>End Date</label>
          <p className="text-muted small mt-2">
            End date determined automatically from subsequent contract's start date. When creating a rate, the previous
            rate ends the day before this start date for the same customer and size type.
          </p>
        </div>
      </div>
    </div>
  );
};

const EmptyContainerAndChassisCard = () => {
  const context = useContext(EditContractContext);
  return (
    <div className="card-primary">
      <div className="card-header">
        <h3>Setup</h3>
      </div>
      <div className="card-body">
        <div className="grid-2">
          <h4>Free Days</h4>
          <input
            type="number"
            required
            step={1}
            min={0}
            value={context.contract.freeDays}
            onChange={e => context.editContract({ freeDays: Number(e.target.value ?? 0) })}
          />
        </div>
        <div className="grid-2">
          <h4>Daily Rate</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.dailyEmpty}
            onChange={e => context.editContract({ dailyEmpty: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Gate In</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.gateInEmpty}
            onChange={e => context.editContract({ gateInEmpty: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Gate Out</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.gateOutEmpty}
            onChange={e => context.editContract({ gateOutEmpty: Number(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
};

const EmptyBoxOnlyCard = () => {
  const context = useContext(EditContractContext);
  return (
    <div className="card-primary">
      <div className="card-header">
        <h3>Empty Container</h3>
      </div>
      <div className="card-body">
        <div className="grid-2">
          <h4>Free Days</h4>
          <p className="text-muted">{context.contract.freeDays}</p>
        </div>
        <div className="grid-2">
          <h4>Daily Rate</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.dailyEmptyContainer}
            onChange={e => context.editContract({ dailyEmptyContainer: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Gate In</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.gateInEmptyContainer}
            onChange={e => context.editContract({ gateInEmptyContainer: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Gate Out</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.gateOutEmptyContainer}
            onChange={e => context.editContract({ gateOutEmptyContainer: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Lift Off</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.liftOffEmpty}
            onChange={e => context.editContract({ liftOffEmpty: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Lift On</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.liftOnEmpty}
            onChange={e => context.editContract({ liftOnEmpty: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Dig</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.digEmpty}
            onChange={e => context.editContract({ digEmpty: Number(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
};

const ChassisOnlyCard = () => {
  const context = useContext(EditContractContext);
  return (
    <div className="card-primary">
      <div className="card-header">
        <h3>Chassis</h3>
      </div>
      <div className="card-body">
        <div className="grid-2">
          <h4>Free Days</h4>
          <p className="text-muted">{context.contract.freeDays}</p>
        </div>
        <div className="grid-2">
          <h4>Daily Rate</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.dailyChassis}
            onChange={e => context.editContract({ dailyChassis: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Gate In</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.gateInChassis}
            onChange={e => context.editContract({ gateInChassis: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Gate Out</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.gateOutChassis}
            onChange={e => context.editContract({ gateOutChassis: Number(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
};

const LoadedUnitCard = () => {
  const context = useContext(EditContractContext);
  return (
    <div className="card-primary">
      <div className="card-header">
        <h3>Loaded</h3>
      </div>
      <div className="card-body">
        <div className="grid-2">
          <h4>Free Days</h4>
          <input
            type="number"
            required
            step={1}
            min={0}
            value={context.contract.freeDaysLoaded}
            onChange={e => context.editContract({ freeDaysLoaded: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Daily Rate</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.dailyLoaded}
            onChange={e => context.editContract({ dailyLoaded: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Gate In</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.gateInLoaded}
            onChange={e => context.editContract({ gateInLoaded: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Gate Out</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.gateOutLoaded}
            onChange={e => context.editContract({ gateOutLoaded: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Lift Off</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.liftOffLoaded}
            onChange={e => context.editContract({ liftOffLoaded: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Lift On</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.liftOnLoaded}
            onChange={e => context.editContract({ liftOnLoaded: Number(e.target.value) })}
          />
        </div>
        <div className="grid-2">
          <h4>Dig</h4>
          <input
            type="number"
            required
            step={'any'}
            min={0}
            value={context.contract.digLoaded}
            onChange={e => context.editContract({ digLoaded: Number(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
};

export default EditContract;
